import { useState, useCallback, useEffect, useRef } from 'react'
import { useAI } from '../contexts/AIContext'
import { useOrganization } from '../contexts/OrganizationContext'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

export interface VoiceCommandResult {
  success: boolean
  action?: string
  status?: 'ready_for_confirmation' | 'executed' | 'needs_clarification'
  booking?: any
  registration?: any
  clarification?: {
    type: string
    question: string
    options?: any[]
  }
  conflict?: any
  error?: string
  message?: string
}

export interface VoiceCommand {
  id: string
  rawText: string
  status: string
  result?: VoiceCommandResult
  createdAt: string
}

export type VoiceCommandState = 'idle' | 'listening' | 'processing' | 'ready' | 'executing' | 'completed' | 'error'

export function useVoiceCommand() {
  const { sendMessage, isLoading, messages } = useAI()
  const { organization } = useOrganization()
  const { user } = useAuth()

  const [currentCommand, setCurrentCommand] = useState<VoiceCommand | null>(null)
  const [recentCommands, setRecentCommands] = useState<VoiceCommand[]>([])
  const [lastResult, setLastResult] = useState<VoiceCommandResult | null>(null)
  const [commandState, setCommandState] = useState<VoiceCommandState>('idle')
  const messagesRef = useRef(messages)

  // Keep messagesRef in sync
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  const loadRecentCommands = useCallback(async () => {
    if (!organization?.id) return

    const { data } = await supabase
      .from('voice_commands')
      .select('*')
      .eq('organization_id', organization.id)
      .order('created_at', { ascending: false })
      .limit(10)

    if (data) {
      setRecentCommands(data.map(cmd => ({
        id: cmd.id,
        rawText: cmd.raw_text,
        status: cmd.status,
        result: cmd.action_payload,
        createdAt: cmd.created_at
      })))
    }
  }, [organization?.id])

  const processCommand = useCallback(async (text: string): Promise<VoiceCommandResult> => {
    if (!organization?.id || !user?.id) {
      setCommandState('error')
      return { success: false, error: 'Not authenticated' }
    }

    setCommandState('processing')

    // Create command record
    const { data: command, error: insertError } = await supabase
      .from('voice_commands')
      .insert({
        organization_id: organization.id,
        created_by: user.id,
        input_type: 'text',
        raw_text: text,
        status: 'processing'
      })
      .select()
      .single()

    if (insertError || !command) {
      console.error('Failed to create command:', insertError)
      setCommandState('error')
      return { success: false, error: 'Failed to create command record' }
    }

    setCurrentCommand({
      id: command.id,
      rawText: text,
      status: 'processing',
      createdAt: command.created_at
    })

    try {
      // Send to AI with booking-focused tools
      await sendMessage(text, {
        tools: ['booking_actions', 'member_lookup', 'schedule_query'],
        systemPrompt: `You are a booking assistant for ${organization.name || 'this facility'}.
The user wants to perform a booking action. Use the booking_actions tool to help them.
Today's date is ${new Date().toISOString().split('T')[0]}.
Always use dryRun=true first to show what will happen before confirming.
When you get a result from the booking_actions tool, return the raw JSON result so the UI can display it properly.
If the action is ready for confirmation (status='ready_for_confirmation'), include all the booking details.`
      })

      // Wait for response to process
      await new Promise(resolve => setTimeout(resolve, 500))

      // Parse result from the last assistant message
      let result: VoiceCommandResult = { success: false, error: 'No result from AI' }

      // Get the latest messages from ref
      const latestMessages = messagesRef.current
      const lastAssistantMessage = [...latestMessages].reverse().find(m => m.role === 'assistant')

      if (lastAssistantMessage?.content) {
        // Try to extract structured result from AI response
        try {
          // Look for JSON blocks in the response
          const jsonMatches = lastAssistantMessage.content.match(/\{[\s\S]*?\}/g)
          if (jsonMatches) {
            // Try each JSON match to find a valid result
            for (const jsonStr of jsonMatches) {
              try {
                const parsed = JSON.parse(jsonStr)
                if (parsed.success !== undefined || parsed.status || parsed.booking || parsed.registration || parsed.clarification) {
                  result = parsed
                  break
                }
              } catch {
                // Continue to next match
              }
            }
          }

          // If still no structured result, treat text as message
          if (result.error === 'No result from AI') {
            result = {
              success: true,
              message: lastAssistantMessage.content,
              status: undefined // Not a confirmable action
            }
          }
        } catch {
          // If parsing fails, use text response
          result = { success: true, message: lastAssistantMessage.content }
        }
      }

      // Update command record with result
      await supabase
        .from('voice_commands')
        .update({
          status: result.status === 'ready_for_confirmation' ? 'ready' :
                  result.clarification ? 'needs_clarification' :
                  result.success ? 'executed' : 'failed',
          parsed_intent: result,
          action_payload: result,
          processed_at: new Date().toISOString(),
          error_message: result.error
        })
        .eq('id', command.id)

      setCurrentCommand(prev => prev ? {
        ...prev,
        status: result.status || 'processed',
        result
      } : null)

      // Set command state based on result
      if (result.status === 'ready_for_confirmation') {
        setCommandState('ready')
      } else if (result.clarification) {
        setCommandState('ready') // Needs user input
      } else if (result.success) {
        setCommandState('completed')
      } else {
        setCommandState('error')
      }

      setLastResult(result)
      return result
    } catch (err: any) {
      console.error('Voice command error:', err)

      await supabase
        .from('voice_commands')
        .update({
          status: 'failed',
          error_message: err.message
        })
        .eq('id', command.id)

      setCommandState('error')
      const errorResult = { success: false, error: err.message }
      setLastResult(errorResult)
      return errorResult
    }
  }, [organization, user, sendMessage])

  const confirmAction = useCallback(async (commandId: string, payload: any): Promise<VoiceCommandResult> => {
    if (!organization?.id) {
      setCommandState('error')
      return { success: false, error: 'Not authenticated' }
    }

    setCommandState('executing')

    // Build confirmation message asking AI to execute with dryRun=false
    const actionType = payload.action || 'create_booking'
    const confirmMessage = `Execute the confirmed ${actionType} action with dryRun=false. Details: ${JSON.stringify({
      ...payload,
      dryRun: false
    })}`

    await sendMessage(confirmMessage, {
      tools: ['booking_actions'],
      systemPrompt: `Execute the booking action the user has confirmed. Set dryRun=false to actually perform the action.
Return the raw JSON result so the UI can display the success message.`
    })

    // Wait for response
    await new Promise(resolve => setTimeout(resolve, 500))

    let result: VoiceCommandResult = { success: false, error: 'No result' }

    const latestMessages = messagesRef.current
    const lastAssistantMessage = [...latestMessages].reverse().find(m => m.role === 'assistant')

    if (lastAssistantMessage?.content) {
      try {
        const jsonMatches = lastAssistantMessage.content.match(/\{[\s\S]*?\}/g)
        if (jsonMatches) {
          for (const jsonStr of jsonMatches) {
            try {
              const parsed = JSON.parse(jsonStr)
              if (parsed.success !== undefined || parsed.status === 'executed') {
                result = parsed
                break
              }
            } catch {
              // Continue
            }
          }
        }

        if (result.error === 'No result') {
          result = { success: true, status: 'executed', message: lastAssistantMessage.content }
        }
      } catch {
        result = { success: true, status: 'executed', message: lastAssistantMessage.content }
      }
    }

    // Update command record
    await supabase
      .from('voice_commands')
      .update({
        status: result.success ? 'executed' : 'failed',
        result_id: result.booking?.id || result.registration?.id,
        result_type: result.action,
        confirmed_at: new Date().toISOString(),
        executed_at: result.success ? new Date().toISOString() : null
      })
      .eq('id', commandId)

    setCommandState(result.success ? 'completed' : 'error')
    setLastResult(result)
    return result
  }, [organization, sendMessage])

  const cancelCommand = useCallback(async (commandId: string) => {
    await supabase
      .from('voice_commands')
      .update({ status: 'cancelled' })
      .eq('id', commandId)

    setCurrentCommand(null)
    setLastResult(null)
    setCommandState('idle')
  }, [])

  const clearResult = useCallback(() => {
    setLastResult(null)
    setCurrentCommand(null)
    setCommandState('idle')
  }, [])

  return {
    currentCommand,
    recentCommands,
    isProcessing: isLoading,
    lastResult,
    commandState,
    processCommand,
    confirmAction,
    cancelCommand,
    loadRecentCommands,
    clearResult
  }
}
